import Dialog from "../../../General/ConfirmableDialog";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Switch,
  FormControlLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import axios from "axios";
import { Dayjs } from "dayjs";
import {
  functionAppUrlVariable,
  generateAllRepairReportFunctionKey,
} from "../../../../api/apiVariable";
import { useState } from "react";
import StatusSnackbar from "../../../General/StatusSnackbar/StatusSnackbar";
import { PaymentMethod } from "../../../../data/Fleet/PaymentMethod";
import {
  getIncidentTypesForAsset,
  INCIDENT_TYPE_LABEL_MAP,
} from "../../../../data/Fleet/IncidentType";

const generateAllRepairsReportPdf = async (
  token: string | null | undefined,
  startDate: Dayjs | null,
  endDate: Dayjs | null,
  isForTrucks: boolean,
  incidentsRepairsOnly?: boolean | null,
  make?: string | null,
  model?: string | null,
  transactionType?: string | null,
  paymentMethods?: string[],
  incidentTypes?: string[],
  includeMaintenancePerMile?: boolean,
): Promise<Blob> => {
  const params: Record<string, any> = {
    startDate: startDate?.format("YYYY-MM-DD"),
    endDate: endDate?.format("YYYY-MM-DD"),
    isForTrucks,
    incidentsRepairsOnly,
    make,
    model,
    rentTransactionType: transactionType || null,
    paymentMethods: paymentMethods,
    incidentTypes,
    includeMaintenancePerMile,
    code: generateAllRepairReportFunctionKey,
  };

  const response = await axios.get(
    `${functionAppUrlVariable}/generateAllRepairReport`,
    {
      headers: { Authorization: `Bearer ${token}` },
      params,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        Object.keys(params).forEach((key) => {
          const value = params[key];
          if (Array.isArray(value)) {
            value.forEach((v) => searchParams.append(key, v));
          } else if (value !== null && value !== undefined) {
            searchParams.append(key, value);
          }
        });
        return searchParams.toString();
      },
      responseType: "blob",
    },
  );

  return response.data;
};

interface GenerateAllAssetRepairsButtonProps {
  token: string | null | undefined;
  incidentsRepairsOnly?: boolean | null;
}

const GenerateAllAssetRepairsButton: React.FC<
  GenerateAllAssetRepairsButtonProps
> = ({ token, incidentsRepairsOnly }) => {
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [isDateRangePickerOpen, setIsDateRangePickerOpen] = useState(false);
  const [make, setMake] = useState<string | null>(null);
  const [model, setModel] = useState<string | null>(null);
  const [transactionType, setTransactionType] = useState<string | null>(null);
  const [isForTrucks, setIsForTrucks] = useState<boolean>(true);

  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  const [incidentTypes, setIncidentTypes] = useState<string[]>([]);
  const [includeMaintenancePerMile, setIncludeMaintenancePerMile] =
    useState(false);

  const handleSnackbarClose = () => setStatus("idle");

  const handleGenerateRepairPdf = async () => {
    if (!startDate || !endDate) {
      alert("Please select a date range.");
      return;
    }
    setLoading(true);
    try {
      const blob = await generateAllRepairsReportPdf(
        token,
        startDate,
        endDate,
        isForTrucks,
        incidentsRepairsOnly,
        make,
        model,
        transactionType,
        paymentMethods,
        incidentTypes,
        includeMaintenancePerMile,
      );

      const url = window.URL.createObjectURL(
        new Blob([blob], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${
          incidentsRepairsOnly === true
            ? `OTR_Repairs_Report_${startDate.format(
                "YYYY-MM-DD",
              )}_${endDate.format("YYYY-MM-DD")}`
            : incidentsRepairsOnly === false
              ? `Yard_Repairs_Report_${startDate.format(
                  "YYYY-MM-DD",
                )}_${endDate.format("YYYY-MM-DD")}`
              : `All_Repairs_Report_${startDate.format(
                  "YYYY-MM-DD",
                )}_${endDate.format("YYYY-MM-DD")}`
        }_${transactionType || "AllTypes"}_${
          isForTrucks ? "Trucks" : "Trailers"
        }_${make || "All_Makes"}_${model || "All_Models"}.pdf`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setStatus("success");
      setIsDateRangePickerOpen(false);
    } catch (error: any) {
      setStatus("error");
      if (error.response?.status === 500) {
        setErrorMessage("No repairs found for this selection");
      } else {
        setErrorMessage(error.response?.data || "Failed to generate PDF");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={() => setIsDateRangePickerOpen(true)}
      >
        {incidentsRepairsOnly === true
          ? "Generate OTR Report PDF"
          : incidentsRepairsOnly === false
            ? "Generate Yard Repairs Report PDF"
            : "Generate All Repairs Report PDF"}
      </Button>

      <Dialog
        open={isDateRangePickerOpen}
        onClose={() => setIsDateRangePickerOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Select Date Range</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                slotProps={{ textField: { variant: "outlined" } }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                slotProps={{ textField: { variant: "outlined" } }}
              />

              <FormControlLabel
                label={isForTrucks ? "Trucks" : "Trailers"}
                control={
                  <Switch
                    color="primary"
                    checked={isForTrucks}
                    onChange={(e) => {
                      setIsForTrucks(e.target.checked);
                      setIncidentTypes([]);
                    }}
                  />
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeMaintenancePerMile}
                    onChange={(event) =>
                      setIncludeMaintenancePerMile(event.target.checked)
                    }
                  />
                }
                label="Include Maintenance Per Mile"
              />
            </Box>
          </LocalizationProvider>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label="Make"
              variant="outlined"
              value={make || ""}
              onChange={(e) => setMake(e.target.value || null)}
            />
            <TextField
              label="Model"
              variant="outlined"
              value={model || ""}
              onChange={(e) => setModel(e.target.value || null)}
            />
            <FormControl fullWidth variant="outlined">
              <InputLabel id="transaction-type-label">
                Transaction Type
              </InputLabel>
              <Select
                labelId="transaction-type-label"
                label="Transaction Type"
                value={transactionType || ""}
                onChange={(e) => setTransactionType(e.target.value || null)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                <MenuItem value="RENT-IN-LOCAL">Rent In Local</MenuItem>
                <MenuItem value="RENT-IN-OTR">Rent In OTR</MenuItem>
                <MenuItem value="L2B">L2B</MenuItem>
                <MenuItem value="RENT-OUT">Rent Out</MenuItem>
                <MenuItem value="LEASE-OUT">Lease Out</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="payment-method-label">
                Payment Method(s)
              </InputLabel>

              <Select
                labelId="payment-method-label"
                label="Payment Method(s)"
                multiple
                value={paymentMethods}
                onChange={(e) => setPaymentMethods(e.target.value as string[])}
                renderValue={(selected) =>
                  (selected as string[])
                    .map(
                      (v) =>
                        PaymentMethod.find((opt) => opt.value === v)?.label,
                    )
                    .join(", ")
                }
              >
                {PaymentMethod.filter((opt) => opt.value !== "").map(
                  ({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      <Checkbox checked={paymentMethods.indexOf(value) > -1} />
                      <ListItemText primary={label} />
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="incident-type-label">Incident Type(s)</InputLabel>

              <Select
                labelId="incident-type-label"
                label="Incident Type(s)"
                multiple
                value={incidentTypes}
                onChange={(e) => setIncidentTypes(e.target.value as string[])}
                renderValue={(selected) =>
                  (selected as string[])
                    .map((v) => INCIDENT_TYPE_LABEL_MAP[v])
                    .join(", ")
                }
              >
                {getIncidentTypesForAsset(isForTrucks ? "truck" : "trailer")
                  .filter(({ value }) => value)
                  .map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      <Checkbox checked={incidentTypes.indexOf(value) > -1} />
                      <ListItemText primary={label} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setIsDateRangePickerOpen(false)}
            color="secondary"
            disabled={loading}
            variant="contained"
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerateRepairPdf}
            color="primary"
            disabled={loading}
            variant="contained"
          >
            {loading ? <CircularProgress size={24} /> : "Generate PDF"}
          </Button>
        </DialogActions>
      </Dialog>

      <StatusSnackbar
        status={status}
        successMessage="Repairs report PDF generated successfully"
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default GenerateAllAssetRepairsButton;
